import CustomRadioButton from "@/components/Button/CustomRadioButton";
import { RadioGroup } from "@/components/ui/radio-group";
import { memo, useCallback, useMemo } from "react";
import type { FieldRenderInputProps } from "../../types";
import { getAttributeOptions } from "../../utils/option-utils";
import { extractValueFromValueLabel } from "../../utils/value-label-utils";
import type { BaseFieldProps } from "./BaseField";
import { BaseField } from "./BaseField";

/** 選択オプションの型 */
interface RadioOption {
  value: string;
  label: string;
}

interface RadioFieldProps extends BaseFieldProps {
  options?: RadioOption[];
}

/**
 * ラジオボタン入力フィールドコンポーネント
 * 複数の選択肢から一つを選択するためのフィールド
 */
export const RadioField = memo(
  ({
    fieldId,
    field,
    layout,
    readOnly,
    disabled,
    className,
    options,
    "data-testid": testId,
  }: RadioFieldProps) => {
    // 属性から選択肢を取得
    const radioOptions = useMemo(() => {
      // オプションが提供されている場合はそれを使用
      if (options && options.length > 0) {
        return options;
      }

      if (field) {
        return getAttributeOptions(field);
      }

      return [];
    }, [field, options]);

    // 入力レンダリング関数
    const renderInput = useCallback(
      (props: FieldRenderInputProps) => {
        const stringValue = extractValueFromValueLabel(props.value);

        const handleValueChange = (value: string) => {
          props.onChange(value);
        };

        return (
          <RadioGroup
            value={stringValue}
            onValueChange={handleValueChange}
            onFocus={props.onFocus}
            onBlur={props.onBlur}
            disabled={readOnly}
          >
            {radioOptions.map((option: RadioOption) => (
              <CustomRadioButton
                key={option.value}
                value={option.value}
                checked={stringValue === option.value}
                label={option.label}
                onChange={handleValueChange}
              />
            ))}
          </RadioGroup>
        );
      },
      [radioOptions, readOnly],
    );

    // BaseFieldを使用してラジオフィールドをレンダリング
    return (
      <BaseField
        fieldId={fieldId}
        field={field}
        layout={layout}
        readOnly={readOnly}
        disabled={disabled}
        className={className}
        data-testid={testId}
        renderInput={renderInput}
      />
    );
  },
);

RadioField.displayName = "RadioField";
